import { inject, Injectable } from '@angular/core';
import { Rule } from 'src/app/interfaces/rule';
import { APIProviderService } from '../api/api.provider.service';
import { ValidAttributeStore } from 'src/app/interfaces/valid-attribute-store';
import { assert } from 'src/app/utils/assert.util';
import { ConfiguredItem } from 'src/app/interfaces/configured-item';
import { ReachableAttributeStore } from 'src/app/interfaces/reachable-attribute-store';
import { StoneTypesWithShapesService } from 'src/app/services/stone-types-with-shapes/stone-types-with-shapes.service';
import { StoneArrangementsWithSettingsService } from 'src/app/services/stone-arrangementsWithSettings/stone-arrangements-with-settings.service';

@Injectable({
  providedIn: 'root'
})
export class RulesProviderService extends APIProviderService<Rule> {
  private stoneArrangementsWithSettingsService = inject(StoneArrangementsWithSettingsService);
  private stoneTypesWithShapesService = inject(StoneTypesWithShapesService);

  protected override get attributeURL(): string {
    return `${super.attributeURL}/Rules`;
  }

  private get apiControllerURL() {
    return `${this.baseURL}${this.attributeURL}`;
  }

  async getIsValid(configuredItem: ConfiguredItem, retailerIframeID: string | null): Promise<boolean> {
    const url = new URL(`${this.apiControllerURL}/IsValid`);
    assert(configuredItem.design, 'Design was null');
    assert(configuredItem.form, 'form was null');
    assert(configuredItem.primaryMaterial, 'primaryMaterial was null');
    assert(configuredItem.secondaryMaterial, 'secondaryMaterial was null');
    assert(configuredItem.width, 'width was null');
    assert(configuredItem.size, 'size was null');
    url.searchParams.set('designID', configuredItem.design.id.toString());
    url.searchParams.set('formID', configuredItem.form.id.toString());
    url.searchParams.set('insideMaterialID', configuredItem.primaryMaterial.id.toString());
    url.searchParams.set('outsideMaterialID', configuredItem.secondaryMaterial.id.toString());
    url.searchParams.set('widthID', configuredItem.width.id.toString());
    url.searchParams.set('sizeID', configuredItem.size.id.toString());
    url.searchParams.set('hasEngraving', (!!configuredItem.engravingText && !!configuredItem.engravingType).toString());
    if (configuredItem.gemstoneArrangement) {
      assert(configuredItem.gemstoneSize, 'No stone size selected');
      const stoneTypeWithShape = await this.getSelectedStoneTypeWithShape(configuredItem);
      const stoneArrangementWithSetting = await this.getselectedStoneArrangementWithSetting(configuredItem);
      url.searchParams.set('stoneTypeWithShapeID', stoneTypeWithShape.id.toString());
      url.searchParams.set('stoneArrangementWithSettingID', stoneArrangementWithSetting.id.toString());
      url.searchParams.set('stoneSizeID', configuredItem.gemstoneSize.id.toString());
    }
    if (retailerIframeID) {
      url.searchParams.set('retailerIframeID', retailerIframeID);
    }
    const isValid = await this.webProvider.get<boolean>(url.href);
    assert(isValid, 'Failed to determine validity');
    return isValid;
  }

  async getValidAttributes(configuredItem: ConfiguredItem, retailerIframeID: string | null): Promise<ValidAttributeStore> {
    const url = new URL(`${this.apiControllerURL}/ValidAttributes`);
    assert(configuredItem.design, 'Design was null');
    assert(configuredItem.form, 'form was null');
    assert(configuredItem.primaryMaterial, 'primaryMaterial was null');
    assert(configuredItem.secondaryMaterial, 'secondaryMaterial was null');
    assert(configuredItem.width, 'width was null');
    assert(configuredItem.size, 'size was null');
    url.searchParams.set('designID', configuredItem.design.id.toString());
    url.searchParams.set('formID', configuredItem.form.id.toString());
    url.searchParams.set('insideMaterialID', configuredItem.primaryMaterial.id.toString());
    url.searchParams.set('outsideMaterialID', configuredItem.secondaryMaterial.id.toString());
    url.searchParams.set('widthID', configuredItem.width.id.toString());
    url.searchParams.set('sizeID', configuredItem.size.id.toString());
    url.searchParams.set('hasEngraving', (!!configuredItem.engravingText && !!configuredItem.engravingType).toString());
    if (configuredItem.gemstoneArrangement) {
      assert(configuredItem.gemstoneSize, 'No stone size selected');
      const stoneTypeWithShape = await this.getSelectedStoneTypeWithShape(configuredItem);
      const stoneArrangementWithSetting = await this.getselectedStoneArrangementWithSetting(configuredItem);
      url.searchParams.set('stoneTypeWithShapeID', stoneTypeWithShape.id.toString());
      url.searchParams.set('stoneArrangementWithSettingID', stoneArrangementWithSetting.id.toString());
      url.searchParams.set('stoneSizeID', configuredItem.gemstoneSize.id.toString());
    }
    if (retailerIframeID) {
      url.searchParams.set('retailerIframeID', retailerIframeID);
    }
    const validAttributes = await this.webProvider.get<ValidAttributeStore>(url.href);
    assert(validAttributes, 'Failed to fetch attribute');
    return validAttributes;
  }

  async getIsSinglePart(configuredItem: ConfiguredItem, retailerIframeID: string | null): Promise<boolean> {
    const url = new URL(`${this.apiControllerURL}/IsSinglePart`);
    assert(configuredItem.design, 'Design was null');
    assert(configuredItem.form, 'form was null');
    assert(configuredItem.primaryMaterial, 'primaryMaterial was null');
    assert(configuredItem.width, 'width was null');
    assert(configuredItem.size, 'size was null');
    url.searchParams.set('designID', configuredItem.design.id.toString());
    url.searchParams.set('formID', configuredItem.form.id.toString());
    url.searchParams.set('insideMaterialID', configuredItem.primaryMaterial.id.toString());
    url.searchParams.set('widthID', configuredItem.width.id.toString());
    url.searchParams.set('sizeID', configuredItem.size.id.toString());
    url.searchParams.set('hasEngraving', (!!configuredItem.engravingText && !!configuredItem.engravingType).toString());
    if (retailerIframeID) {
      url.searchParams.set('retailerIframeID', retailerIframeID);
    }
    const isSingle = await this.webProvider.get<boolean>(url.href);
    assert(isSingle, 'Failed to fetch isSinglePart');
    return isSingle;
  }

  async getAvailableSizes(configuredItem: ConfiguredItem, retailerIframeID: string | null): Promise<number[]> {
    const url = new URL(`${this.apiControllerURL}/ValidSizes`);
    assert(configuredItem.design, 'Design was null');
    assert(configuredItem.form, 'form was null');
    assert(configuredItem.primaryMaterial, 'primaryMaterial was null');
    assert(configuredItem.secondaryMaterial, 'secondaryMaterial was null');
    assert(configuredItem.width, 'width was null');
    url.searchParams.set('designID', configuredItem.design.id.toString());
    url.searchParams.set('formID', configuredItem.form.id.toString());
    url.searchParams.set('insideMaterialID', configuredItem.primaryMaterial.id.toString());
    url.searchParams.set('outsideMaterialID', configuredItem.secondaryMaterial.id.toString());
    url.searchParams.set('widthID', configuredItem.width.id.toString());
    url.searchParams.set('hasEngraving', (!!configuredItem.engravingText && !!configuredItem.engravingType).toString());
    if (configuredItem.gemstoneArrangement) {
      assert(configuredItem.gemstoneSize, 'No stone size selected');
      const stoneTypeWithShape = await this.getSelectedStoneTypeWithShape(configuredItem);
      const stoneArrangementWithSetting = await this.getselectedStoneArrangementWithSetting(configuredItem);
      url.searchParams.set('stoneTypeWithShapeID', stoneTypeWithShape.id.toString());
      url.searchParams.set('stoneArrangementWithSettingID', stoneArrangementWithSetting.id.toString());
      url.searchParams.set('stoneSizeID', configuredItem.gemstoneSize.id.toString());
    }
    if (retailerIframeID) {
      url.searchParams.set('retailerIframeID', retailerIframeID);
    }
    const availableSizes = await this.webProvider.get<number[]>(url.href);
    assert(availableSizes, 'Failed to fetch available sizes');
    return availableSizes;
  }

  async getAvailableOutsideMaterials(configuredItem: ConfiguredItem, retailerIframeID: string | null): Promise<number[]> {
    const url = new URL(`${this.apiControllerURL}/ValidOutsideMaterials`);
    assert(configuredItem.design, 'Design was null');
    assert(configuredItem.form, 'form was null');
    assert(configuredItem.primaryMaterial, 'primaryMaterial was null');
    assert(configuredItem.secondaryMaterial, 'secondaryMaterial was null');
    assert(configuredItem.width, 'width was null');
    assert(configuredItem.size, 'size was null');
    url.searchParams.set('designID', configuredItem.design.id.toString());
    url.searchParams.set('formID', configuredItem.form.id.toString());
    url.searchParams.set('insideMaterialID', configuredItem.primaryMaterial.id.toString());
    url.searchParams.set('widthID', configuredItem.width.id.toString());
    url.searchParams.set('sizeID', configuredItem.size.id.toString());
    url.searchParams.set('hasEngraving', (!!configuredItem.engravingText && !!configuredItem.engravingType).toString());
    if (configuredItem.gemstoneArrangement) {
      assert(configuredItem.gemstoneSize, 'No stone size selected');
      const stoneTypeWithShape = await this.getSelectedStoneTypeWithShape(configuredItem);
      const stoneArrangementWithSetting = await this.getselectedStoneArrangementWithSetting(configuredItem);
      url.searchParams.set('stoneTypeWithShapeID', stoneTypeWithShape.id.toString());
      url.searchParams.set('stoneArrangementWithSettingID', stoneArrangementWithSetting.id.toString());
      url.searchParams.set('stoneSizeID', configuredItem.gemstoneSize.id.toString());
    }
    if (retailerIframeID) {
      url.searchParams.set('retailerIframeID', retailerIframeID);
    }
    const validAttributes = await this.webProvider.get<number[]>(url.href);
    assert(validAttributes, 'Failed to fetch attribute');
    return validAttributes;
  }

  async getAvailableStoneArrangementsWithSettings(configuredItem: ConfiguredItem, retailerIframeID: string | null): Promise<number[]> {
    const url = new URL(`${this.apiControllerURL}/ValidStoneArrangementsWithSettings`);
    assert(configuredItem.design, 'Design was null');
    assert(configuredItem.form, 'form was null');
    assert(configuredItem.primaryMaterial, 'primaryMaterial was null');
    assert(configuredItem.secondaryMaterial, 'secondaryMaterial was null');
    assert(configuredItem.width, 'width was null');
    assert(configuredItem.size, 'size was null');
    url.searchParams.set('designID', configuredItem.design.id.toString());
    url.searchParams.set('formID', configuredItem.form.id.toString());
    url.searchParams.set('insideMaterialID', configuredItem.primaryMaterial.id.toString());
    url.searchParams.set('outsideMaterialID', configuredItem.secondaryMaterial.id.toString());
    url.searchParams.set('widthID', configuredItem.width.id.toString());
    url.searchParams.set('sizeID', configuredItem.size.id.toString());
    url.searchParams.set('hasEngraving', (!!configuredItem.engravingText && !!configuredItem.engravingType).toString());
    if (configuredItem.gemstoneArrangement) {
      assert(configuredItem.gemstoneSize, 'No stone size selected');
      const stoneTypeWithShape = await this.getSelectedStoneTypeWithShape(configuredItem);
      const stoneArrangementWithSetting = await this.getselectedStoneArrangementWithSetting(configuredItem);
      url.searchParams.set('stoneTypeWithShapeID', stoneTypeWithShape.id.toString());
      url.searchParams.set('stoneArrangementWithSettingID', stoneArrangementWithSetting.id.toString());
      url.searchParams.set('stoneSizeID', configuredItem.gemstoneSize.id.toString());
    }
    if (retailerIframeID) {
      url.searchParams.set('retailerIframeID', retailerIframeID);
    }
    const validAttributes = await this.webProvider.get<number[]>(url.href);
    assert(validAttributes, 'Failed to fetch attribute');
    return validAttributes;
  }

  async getAvailableStoneSizes(configuredItem: ConfiguredItem, retailerIframeID: string | null): Promise<number[]> {
    const url = new URL(`${this.apiControllerURL}/ValidStoneSizes`);
    assert(configuredItem.design, 'Design was null');
    assert(configuredItem.form, 'form was null');
    assert(configuredItem.primaryMaterial, 'primaryMaterial was null');
    assert(configuredItem.secondaryMaterial, 'secondaryMaterial was null');
    assert(configuredItem.width, 'width was null');
    assert(configuredItem.size, 'size was null');
    url.searchParams.set('designID', configuredItem.design.id.toString());
    url.searchParams.set('formID', configuredItem.form.id.toString());
    url.searchParams.set('insideMaterialID', configuredItem.primaryMaterial.id.toString());
    url.searchParams.set('outsideMaterialID', configuredItem.secondaryMaterial.id.toString());
    url.searchParams.set('widthID', configuredItem.width.id.toString());
    url.searchParams.set('sizeID', configuredItem.size.id.toString());
    url.searchParams.set('hasEngraving', (!!configuredItem.engravingText && !!configuredItem.engravingType).toString());
    if (configuredItem.gemstoneArrangement) {
      assert(configuredItem.gemstoneSize, 'No stone size selected');
      const stoneTypeWithShape = await this.getSelectedStoneTypeWithShape(configuredItem);
      const stoneArrangementWithSetting = await this.getselectedStoneArrangementWithSetting(configuredItem);
      url.searchParams.set('stoneTypeWithShapeID', stoneTypeWithShape.id.toString());
      url.searchParams.set('stoneArrangementWithSettingID', stoneArrangementWithSetting.id.toString());
      url.searchParams.set('stoneSizeID', configuredItem.gemstoneSize.id.toString());
    }
    if (retailerIframeID) {
      url.searchParams.set('retailerIframeID', retailerIframeID);
    }
    const validAttributes = await this.webProvider.get<number[]>(url.href);
    assert(validAttributes, 'Failed to fetch attribute');
    return validAttributes;
  }

  async getAvailableStoneTypesWithShapes(configuredItem: ConfiguredItem, retailerIframeID: string | null): Promise<number[]> {
    const url = new URL(`${this.apiControllerURL}/ValidStoneTypesWithShapes`);
    assert(configuredItem.design, 'Design was null');
    assert(configuredItem.form, 'form was null');
    assert(configuredItem.primaryMaterial, 'primaryMaterial was null');
    assert(configuredItem.secondaryMaterial, 'secondaryMaterial was null');
    assert(configuredItem.width, 'width was null');
    assert(configuredItem.size, 'size was null');
    url.searchParams.set('designID', configuredItem.design.id.toString());
    url.searchParams.set('formID', configuredItem.form.id.toString());
    url.searchParams.set('insideMaterialID', configuredItem.primaryMaterial.id.toString());
    url.searchParams.set('outsideMaterialID', configuredItem.secondaryMaterial.id.toString());
    url.searchParams.set('widthID', configuredItem.width.id.toString());
    url.searchParams.set('sizeID', configuredItem.size.id.toString());
    url.searchParams.set('hasEngraving', (!!configuredItem.engravingText && !!configuredItem.engravingType).toString());
    if (configuredItem.gemstoneArrangement) {
      assert(configuredItem.gemstoneSize, 'No stone size selected');
      const stoneTypeWithShape = await this.getSelectedStoneTypeWithShape(configuredItem);
      const stoneArrangementWithSetting = await this.getselectedStoneArrangementWithSetting(configuredItem);
      url.searchParams.set('stoneTypeWithShapeID', stoneTypeWithShape.id.toString());
      url.searchParams.set('stoneArrangementWithSettingID', stoneArrangementWithSetting.id.toString());
      url.searchParams.set('stoneSizeID', configuredItem.gemstoneSize.id.toString());
    }
    if (retailerIframeID) {
      url.searchParams.set('retailerIframeID', retailerIframeID);
    }
    const validAttributes = await this.webProvider.get<number[]>(url.href);
    assert(validAttributes, 'Failed to fetch attribute');
    return validAttributes;
  }

  async getClosestValidItemByDesign(configuredItem: ConfiguredItem, designID: number, retailerIframeID: string | null): Promise<Rule> {
    const url = new URL(`${this.apiControllerURL}/ClosestValidItemByDesign`);
    assert(configuredItem.form, 'No design selected');
    assert(configuredItem.size, 'No size selected');
    assert(configuredItem.primaryMaterial, 'No primary material selected');
    assert(configuredItem.secondaryMaterial, 'No secondary material selected');
    assert(configuredItem.width, 'No width selected');
    url.searchParams.set('designID', designID.toString());
    url.searchParams.set('sizeID', configuredItem.size.id.toString());
    url.searchParams.set('insideMaterialID', configuredItem.primaryMaterial.id.toString());
    url.searchParams.set('outsideMaterialID', configuredItem.secondaryMaterial.id.toString());
    url.searchParams.set('widthID', configuredItem.width.id.toString());
    url.searchParams.set('formID', configuredItem.form.id.toString());
    if (configuredItem.gemstoneArrangement) {
      assert(configuredItem.gemstoneSize, 'No stone size selected');
      const stoneTypeWithShape = await this.getSelectedStoneTypeWithShape(configuredItem);
      const stoneArrangementWithSetting = await this.getselectedStoneArrangementWithSetting(configuredItem);
      url.searchParams.set('stoneTypeWithShapeID', stoneTypeWithShape.id.toString());
      url.searchParams.set('stoneArrangementWithSettingID', stoneArrangementWithSetting.id.toString());
      url.searchParams.set('stoneSizeID', configuredItem.gemstoneSize.id.toString());
    }
    if (retailerIframeID) {
      url.searchParams.set('retailerIframeID', retailerIframeID);
    }
    const validAttributes = await this.webProvider.get<Rule>(url.href);
    assert(validAttributes, 'Failed to fetch attribute');
    return validAttributes;
  }

  async getClosestValidItemBySize(configuredItem: ConfiguredItem, sizeID: number, retailerIframeID: string | null): Promise<Rule> {
    const url = new URL(`${this.apiControllerURL}/ClosestValidItemBySize`);
    assert(configuredItem.form, 'No design selected');
    assert(configuredItem.design, 'No size selected');
    assert(configuredItem.primaryMaterial, 'No primary material selected');
    assert(configuredItem.secondaryMaterial, 'No secondary material selected');
    assert(configuredItem.width, 'No width selected');
    url.searchParams.set('designID', configuredItem.design.id.toString());
    url.searchParams.set('sizeID', sizeID.toString());
    url.searchParams.set('insideMaterialID', configuredItem.primaryMaterial.id.toString());
    url.searchParams.set('outsideMaterialID', configuredItem.secondaryMaterial.id.toString());
    url.searchParams.set('widthID', configuredItem.width.id.toString());
    url.searchParams.set('formID', configuredItem.form.id.toString());
    if (configuredItem.gemstoneArrangement) {
      assert(configuredItem.gemstoneSize, 'No stone size selected');
      const stoneTypeWithShape = await this.getSelectedStoneTypeWithShape(configuredItem);
      const stoneArrangementWithSetting = await this.getselectedStoneArrangementWithSetting(configuredItem);
      url.searchParams.set('stoneTypeWithShapeID', stoneTypeWithShape.id.toString());
      url.searchParams.set('stoneArrangementWithSettingID', stoneArrangementWithSetting.id.toString());
      url.searchParams.set('stoneSizeID', configuredItem.gemstoneSize.id.toString());
    }
    if (retailerIframeID) {
      url.searchParams.set('retailerIframeID', retailerIframeID);
    }
    const validAttributes = await this.webProvider.get<Rule>(url.href);
    assert(validAttributes, 'Failed to fetch attribute');
    return validAttributes;
  }

  async getClosestValidItemByForm(configuredItem: ConfiguredItem, formID: number, retailerIframeID: string | null): Promise<Rule> {
    const url = new URL(`${this.apiControllerURL}/ClosestValidItemByForm`);
    assert(configuredItem.design, 'No design selected');
    assert(configuredItem.size, 'No size selected');
    assert(configuredItem.primaryMaterial, 'No primary material selected');
    assert(configuredItem.secondaryMaterial, 'No secondary material selected');
    assert(configuredItem.width, 'No width selected');
    url.searchParams.set('designID', configuredItem.design.id.toString());
    url.searchParams.set('sizeID', configuredItem.size.id.toString());
    url.searchParams.set('insideMaterialID', configuredItem.primaryMaterial.id.toString());
    url.searchParams.set('outsideMaterialID', configuredItem.secondaryMaterial.id.toString());
    url.searchParams.set('widthID', configuredItem.width.id.toString());
    url.searchParams.set('formID', formID.toString());
    if (configuredItem.gemstoneArrangement) {
      assert(configuredItem.gemstoneSize, 'No stone size selected');
      const stoneTypeWithShape = await this.getSelectedStoneTypeWithShape(configuredItem);
      const stoneArrangementWithSetting = await this.getselectedStoneArrangementWithSetting(configuredItem);
      url.searchParams.set('stoneTypeWithShapeID', stoneTypeWithShape.id.toString());
      url.searchParams.set('stoneArrangementWithSettingID', stoneArrangementWithSetting.id.toString());
      url.searchParams.set('stoneSizeID', configuredItem.gemstoneSize.id.toString());
    }
    if (retailerIframeID) {
      url.searchParams.set('retailerIframeID', retailerIframeID);
    }
    const validAttributes = await this.webProvider.get<Rule>(url.href);
    assert(validAttributes, 'Failed to fetch attribute');
    return validAttributes;
  }

  async getClosestValidItemByWidth(configuredItem: ConfiguredItem, widthID: number, retailerIframeID: string | null): Promise<Rule> {
    const url = new URL(`${this.apiControllerURL}/ClosestValidItemByWidth`);
    assert(configuredItem.design, 'No design selected');
    assert(configuredItem.size, 'No size selected');
    assert(configuredItem.primaryMaterial, 'No primary material selected');
    assert(configuredItem.secondaryMaterial, 'No secondary material selected');
    assert(configuredItem.form, 'No form selected');
    url.searchParams.set('designID', configuredItem.design.id.toString());
    url.searchParams.set('sizeID', configuredItem.size.id.toString());
    url.searchParams.set('insideMaterialID', configuredItem.primaryMaterial.id.toString());
    url.searchParams.set('outsideMaterialID', configuredItem.secondaryMaterial.id.toString());
    url.searchParams.set('formID', configuredItem.form.id.toString());
    url.searchParams.set('widthID', widthID.toString());
    if (configuredItem.gemstoneArrangement) {
      assert(configuredItem.gemstoneSize, 'No stone size selected');
      const stoneTypeWithShape = await this.getSelectedStoneTypeWithShape(configuredItem);
      const stoneArrangementWithSetting = await this.getselectedStoneArrangementWithSetting(configuredItem);
      url.searchParams.set('stoneTypeWithShapeID', stoneTypeWithShape.id.toString());
      url.searchParams.set('stoneArrangementWithSettingID', stoneArrangementWithSetting.id.toString());
      url.searchParams.set('stoneSizeID', configuredItem.gemstoneSize.id.toString());
    }
    if (retailerIframeID) {
      url.searchParams.set('retailerIframeID', retailerIframeID);
    }
    const validAttributes = await this.webProvider.get<Rule>(url.href);
    assert(validAttributes, 'Failed to fetch attribute');
    return validAttributes;
  }

  async getClosestValidItemByPrimaryMaterial(configuredItem: ConfiguredItem, materialID: number, retailerIframeID: string | null): Promise<Rule> {
    const url = new URL(`${this.apiControllerURL}/ClosestValidItemByPrimaryMaterial`);
    assert(configuredItem.design, 'No design selected');
    assert(configuredItem.size, 'No size selected');
    assert(configuredItem.secondaryMaterial, 'No secondary material selected');
    assert(configuredItem.form, 'No form selected');
    assert(configuredItem.width, 'No width selected');
    url.searchParams.set('designID', configuredItem.design.id.toString());
    url.searchParams.set('sizeID', configuredItem.size.id.toString());
    url.searchParams.set('outsideMaterialID', configuredItem.secondaryMaterial.id.toString());
    url.searchParams.set('formID', configuredItem.form.id.toString());
    url.searchParams.set('widthID', configuredItem.width.id.toString());
    url.searchParams.set('insideMaterialID', materialID.toString());
    if (configuredItem.gemstoneArrangement) {
      assert(configuredItem.gemstoneSize, 'No stone size selected');
      const stoneTypeWithShape = await this.getSelectedStoneTypeWithShape(configuredItem);
      const stoneArrangementWithSetting = await this.getselectedStoneArrangementWithSetting(configuredItem);
      url.searchParams.set('stoneTypeWithShapeID', stoneTypeWithShape.id.toString());
      url.searchParams.set('stoneArrangementWithSettingID', stoneArrangementWithSetting.id.toString());
      url.searchParams.set('stoneSizeID', configuredItem.gemstoneSize.id.toString());
    }
    if (retailerIframeID) {
      url.searchParams.set('retailerIframeID', retailerIframeID);
    }
    const validAttributes = await this.webProvider.get<Rule>(url.href);
    assert(validAttributes, 'Failed to fetch attribute');
    return validAttributes;
  }

  async getClosestValidItemBySecondaryMaterial(configuredItem: ConfiguredItem, materialID: number, retailerIframeID: string | null): Promise<Rule> {
    const url = new URL(`${this.apiControllerURL}/ClosestValidItemBySecondaryMaterial`);
    assert(configuredItem.design, 'No design selected');
    assert(configuredItem.size, 'No size selected');
    assert(configuredItem.primaryMaterial, 'No primary material selected');
    assert(configuredItem.width, 'No secondary material selected');
    assert(configuredItem.form, 'No form selected');
    url.searchParams.set('designID', configuredItem.design.id.toString());
    url.searchParams.set('sizeID', configuredItem.size.id.toString());
    url.searchParams.set('insideMaterialID', configuredItem.primaryMaterial.id.toString());
    url.searchParams.set('formID', configuredItem.form.id.toString());
    url.searchParams.set('widthID', configuredItem.width.id.toString());
    url.searchParams.set('outsideMaterialID', materialID.toString());
    if (configuredItem.gemstoneArrangement) {
      assert(configuredItem.gemstoneSize, 'No stone size selected');
      const stoneTypeWithShape = await this.getSelectedStoneTypeWithShape(configuredItem);
      const stoneArrangementWithSetting = await this.getselectedStoneArrangementWithSetting(configuredItem);
      url.searchParams.set('stoneTypeWithShapeID', stoneTypeWithShape.id.toString());
      url.searchParams.set('stoneArrangementWithSettingID', stoneArrangementWithSetting.id.toString());
      url.searchParams.set('stoneSizeID', configuredItem.gemstoneSize.id.toString());
    }
    if (retailerIframeID) {
      url.searchParams.set('retailerIframeID', retailerIframeID);
    }
    const validAttributes = await this.webProvider.get<Rule>(url.href);
    assert(validAttributes, 'Failed to fetch attribute');
    return validAttributes;
  }

    async getClosestValidItemByStoneArrangementWithSetting(configuredItem: ConfiguredItem, stoneArrangementWithSettingID: number, retailerIframeID: string | null): Promise<Rule> {
    const url = new URL(`${this.apiControllerURL}/ClosestByStoneArrangementWithSetting`);
    assert(configuredItem.design, 'Design was null');
    assert(configuredItem.form, 'form was null');
    assert(configuredItem.primaryMaterial, 'primaryMaterial was null');
    assert(configuredItem.secondaryMaterial, 'secondaryMaterial was null');
    assert(configuredItem.width, 'width was null');
    assert(configuredItem.size, 'size was null');
    url.searchParams.set('designID', configuredItem.design.id.toString());
    url.searchParams.set('formID', configuredItem.form.id.toString());
    url.searchParams.set('insideMaterialID', configuredItem.primaryMaterial.id.toString());
    url.searchParams.set('outsideMaterialID', configuredItem.secondaryMaterial.id.toString());
    url.searchParams.set('widthID', configuredItem.width.id.toString());
    url.searchParams.set('sizeID', configuredItem.size.id.toString());
    url.searchParams.set('hasEngraving', (!!configuredItem.engravingText && !!configuredItem.engravingType).toString());
    if (configuredItem.gemstoneArrangement) {
      assert(configuredItem.gemstoneSize, 'No stone size selected');
      const stoneTypeWithShape = await this.getSelectedStoneTypeWithShape(configuredItem);
      url.searchParams.set('stoneTypeWithShapeID', stoneTypeWithShape.id.toString());
      url.searchParams.set('stoneArrangementWithSettingID', stoneArrangementWithSettingID.toString());
      url.searchParams.set('stoneSizeID', configuredItem.gemstoneSize.id.toString());
    }
    if (retailerIframeID) {
      url.searchParams.set('retailerIframeID', retailerIframeID);
    }
    const validAttributes = await this.webProvider.get<Rule>(url.href);
    assert(validAttributes, 'Failed to fetch attribute');
    return validAttributes;
  }

    async getClosestValidItemByStoneSize(configuredItem: ConfiguredItem, stonesizeID: number, retailerIframeID: string | null): Promise<Rule> {
    const url = new URL(`${this.apiControllerURL}/ClosestByStoneSize`);
    assert(configuredItem.design, 'Design was null');
    assert(configuredItem.form, 'form was null');
    assert(configuredItem.primaryMaterial, 'primaryMaterial was null');
    assert(configuredItem.secondaryMaterial, 'secondaryMaterial was null');
    assert(configuredItem.width, 'width was null');
    assert(configuredItem.size, 'size was null');
    url.searchParams.set('designID', configuredItem.design.id.toString());
    url.searchParams.set('formID', configuredItem.form.id.toString());
    url.searchParams.set('insideMaterialID', configuredItem.primaryMaterial.id.toString());
    url.searchParams.set('outsideMaterialID', configuredItem.secondaryMaterial.id.toString());
    url.searchParams.set('widthID', configuredItem.width.id.toString());
    url.searchParams.set('sizeID', configuredItem.size.id.toString());
    url.searchParams.set('hasEngraving', (!!configuredItem.engravingText && !!configuredItem.engravingType).toString());
    if (configuredItem.gemstoneArrangement) {
      const stoneTypeWithShape = await this.getSelectedStoneTypeWithShape(configuredItem);
      const stoneArrangementWithSetting = await this.getselectedStoneArrangementWithSetting(configuredItem);
      url.searchParams.set('stoneTypeWithShapeID', stoneTypeWithShape.id.toString());
      url.searchParams.set('stoneArrangementWithSettingID', stoneArrangementWithSetting.id.toString());
      url.searchParams.set('stoneSizeID', stonesizeID.toString());
    }
    if (retailerIframeID) {
      url.searchParams.set('retailerIframeID', retailerIframeID);
    }
    const validAttributes = await this.webProvider.get<Rule>(url.href);
    assert(validAttributes, 'Failed to fetch attribute');
    return validAttributes;
  }

    async getClosestValidItemByStoneTypeWithShape(configuredItem: ConfiguredItem, stoneTypeWithShapeID: number, retailerIframeID: string | null): Promise<Rule> {
    const url = new URL(`${this.apiControllerURL}/ClosestByStoneTypeWithShape`);
    assert(configuredItem.design, 'Design was null');
    assert(configuredItem.form, 'form was null');
    assert(configuredItem.primaryMaterial, 'primaryMaterial was null');
    assert(configuredItem.secondaryMaterial, 'secondaryMaterial was null');
    assert(configuredItem.width, 'width was null');
    assert(configuredItem.size, 'size was null');
    url.searchParams.set('designID', configuredItem.design.id.toString());
    url.searchParams.set('formID', configuredItem.form.id.toString());
    url.searchParams.set('insideMaterialID', configuredItem.primaryMaterial.id.toString());
    url.searchParams.set('outsideMaterialID', configuredItem.secondaryMaterial.id.toString());
    url.searchParams.set('widthID', configuredItem.width.id.toString());
    url.searchParams.set('sizeID', configuredItem.size.id.toString());
    url.searchParams.set('hasEngraving', (!!configuredItem.engravingText && !!configuredItem.engravingType).toString());
    if (configuredItem.gemstoneArrangement) {
      assert(configuredItem.gemstoneSize, 'No stone size selected');
      const stoneArrangementWithSetting = await this.getselectedStoneArrangementWithSetting(configuredItem);
      url.searchParams.set('stoneTypeWithShapeID', stoneTypeWithShapeID.toString());
      url.searchParams.set('stoneArrangementWithSettingID', stoneArrangementWithSetting.id.toString());
      url.searchParams.set('stoneSizeID', configuredItem.gemstoneSize.id.toString());
    }
    if (retailerIframeID) {
      url.searchParams.set('retailerIframeID', retailerIframeID);
    }
    const validAttributes = await this.webProvider.get<Rule>(url.href);
    assert(validAttributes, 'Failed to fetch attribute');
    return validAttributes;
  }

  async getReachableAttributes(configuredItem: ConfiguredItem, retailerIframeID: string | null): Promise<ReachableAttributeStore> {
    const url = new URL(`${this.apiControllerURL}/ReachableAttributes`);
    assert(configuredItem.design, 'Design was null');
    assert(configuredItem.form, 'form was null');
    assert(configuredItem.primaryMaterial, 'primaryMaterial was null');
    assert(configuredItem.secondaryMaterial, 'secondaryMaterial was null');
    assert(configuredItem.width, 'width was null');
    assert(configuredItem.size, 'size was null');
    url.searchParams.set('designID', configuredItem.design.id.toString());
    url.searchParams.set('formID', configuredItem.form.id.toString());
    url.searchParams.set('insideMaterialID', configuredItem.primaryMaterial.id.toString());
    url.searchParams.set('outsideMaterialID', configuredItem.secondaryMaterial.id.toString());
    url.searchParams.set('widthID', configuredItem.width.id.toString());
    url.searchParams.set('sizeID', configuredItem.size.id.toString());
    url.searchParams.set('hasEngraving', (!!configuredItem.engravingText && !!configuredItem.engravingType).toString());
    if (configuredItem.gemstoneArrangement) {
      assert(configuredItem.gemstoneSize, 'No stone size selected');
      const stoneTypeWithShape = await this.getSelectedStoneTypeWithShape(configuredItem);
      const stoneArrangementWithSetting = await this.getselectedStoneArrangementWithSetting(configuredItem);
      url.searchParams.set('stoneTypeWithShapeID', stoneTypeWithShape.id.toString());
      url.searchParams.set('stoneArrangementWithSettingID', stoneArrangementWithSetting.id.toString());
      url.searchParams.set('stoneSizeID', configuredItem.gemstoneSize.id.toString());
    }
    if (retailerIframeID) {
      url.searchParams.set('retailerIframeID', retailerIframeID);
    }
    const validAttributes = await this.webProvider.get<ReachableAttributeStore>(url.href);
    assert(validAttributes, 'Failed to fetch attribute');
    return validAttributes;
  }

  async getReachableForms(configuredItem: ConfiguredItem, retailerIframeID: string | null): Promise<number[]> {
    const url = new URL(`${this.apiControllerURL}/ReachableForms`);
    assert(configuredItem.design, 'Design was null');
    assert(configuredItem.primaryMaterial, 'primaryMaterial was null');
    assert(configuredItem.secondaryMaterial, 'secondaryMaterial was null');
    assert(configuredItem.width, 'width was null');
    assert(configuredItem.size, 'size was null');
    url.searchParams.set('designID', configuredItem.design.id.toString());
    url.searchParams.set('insideMaterialID', configuredItem.primaryMaterial.id.toString());
    url.searchParams.set('outsideMaterialID', configuredItem.secondaryMaterial.id.toString());
    url.searchParams.set('widthID', configuredItem.width.id.toString());
    url.searchParams.set('sizeID', configuredItem.size.id.toString());
    url.searchParams.set('hasEngraving', (!!configuredItem.engravingText && !!configuredItem.engravingType).toString());
    if (configuredItem.gemstoneArrangement) {
      assert(configuredItem.gemstoneSize, 'No stone size selected');
      const stoneTypeWithShape = await this.getSelectedStoneTypeWithShape(configuredItem);
      const stoneArrangementWithSetting = await this.getselectedStoneArrangementWithSetting(configuredItem);
      url.searchParams.set('stoneTypeWithShapeID', stoneTypeWithShape.id.toString());
      url.searchParams.set('stoneArrangementWithSettingID', stoneArrangementWithSetting.id.toString());
      url.searchParams.set('stoneSizeID', configuredItem.gemstoneSize.id.toString());
    }
    if (retailerIframeID) {
      url.searchParams.set('retailerIframeID', retailerIframeID);
    }
    const validAttributes = await this.webProvider.get<number[]>(url.href);
    assert(validAttributes, 'Failed to fetch attribute');
    return validAttributes;
  }

  async getReachableWidths(configuredItem: ConfiguredItem, retailerIframeID: string | null): Promise<number[]> {
    const url = new URL(`${this.apiControllerURL}/ReachableWidths`);
    assert(configuredItem.design, 'Design was null');
    assert(configuredItem.form, 'form was null');
    assert(configuredItem.primaryMaterial, 'primaryMaterial was null');
    assert(configuredItem.secondaryMaterial, 'secondaryMaterial was null');
    assert(configuredItem.size, 'size was null');
    url.searchParams.set('designID', configuredItem.design.id.toString());
    url.searchParams.set('formID', configuredItem.form.id.toString());
    url.searchParams.set('insideMaterialID', configuredItem.primaryMaterial.id.toString());
    url.searchParams.set('outsideMaterialID', configuredItem.secondaryMaterial.id.toString());
    url.searchParams.set('sizeID', configuredItem.size.id.toString());
    url.searchParams.set('hasEngraving', (!!configuredItem.engravingText && !!configuredItem.engravingType).toString());
    if (configuredItem.gemstoneArrangement) {
      assert(configuredItem.gemstoneSize, 'No stone size selected');
      const stoneTypeWithShape = await this.getSelectedStoneTypeWithShape(configuredItem);
      const stoneArrangementWithSetting = await this.getselectedStoneArrangementWithSetting(configuredItem);
      url.searchParams.set('stoneTypeWithShapeID', stoneTypeWithShape.id.toString());
      url.searchParams.set('stoneArrangementWithSettingID', stoneArrangementWithSetting.id.toString());
      url.searchParams.set('stoneSizeID', configuredItem.gemstoneSize.id.toString());
    }
    if (retailerIframeID) {
      url.searchParams.set('retailerIframeID', retailerIframeID);
    }
    const validAttributes = await this.webProvider.get<number[]>(url.href);
    assert(validAttributes, 'Failed to fetch attribute');
    return validAttributes;
  }

  async getReachablePrimaryMaterials(configuredItem: ConfiguredItem, retailerIframeID: string | null): Promise<number[]> {
    const url = new URL(`${this.apiControllerURL}/ReachablePrimaryMaterials`);
    assert(configuredItem.design, 'Design was null');
    assert(configuredItem.form, 'form was null');
    assert(configuredItem.secondaryMaterial, 'secondaryMaterial was null');
    assert(configuredItem.width, 'width was null');
    assert(configuredItem.size, 'size was null');
    url.searchParams.set('designID', configuredItem.design.id.toString());
    url.searchParams.set('formID', configuredItem.form.id.toString());
    url.searchParams.set('outsideMaterialID', configuredItem.secondaryMaterial.id.toString());
    url.searchParams.set('widthID', configuredItem.width.id.toString());
    url.searchParams.set('sizeID', configuredItem.size.id.toString());
    url.searchParams.set('hasEngraving', (!!configuredItem.engravingText && !!configuredItem.engravingType).toString());
    if (configuredItem.gemstoneArrangement) {
      assert(configuredItem.gemstoneSize, 'No stone size selected');
      const stoneTypeWithShape = await this.getSelectedStoneTypeWithShape(configuredItem);
      const stoneArrangementWithSetting = await this.getselectedStoneArrangementWithSetting(configuredItem);
      url.searchParams.set('stoneTypeWithShapeID', stoneTypeWithShape.id.toString());
      url.searchParams.set('stoneArrangementWithSettingID', stoneArrangementWithSetting.id.toString());
      url.searchParams.set('stoneSizeID', configuredItem.gemstoneSize.id.toString());
    }
    if (retailerIframeID) {
      url.searchParams.set('retailerIframeID', retailerIframeID);
    }
    const validAttributes = await this.webProvider.get<number[]>(url.href);
    assert(validAttributes, 'Failed to fetch attribute');
    return validAttributes;
  }

  async getReachableSecondaryMaterials(configuredItem: ConfiguredItem, retailerIframeID: string | null): Promise<number[]> {
    const url = new URL(`${this.apiControllerURL}/ReachableSecondaryMaterials`);
    assert(configuredItem.design, 'Design was null');
    assert(configuredItem.form, 'form was null');
    assert(configuredItem.primaryMaterial, 'primaryMaterial was null');
    assert(configuredItem.width, 'width was null');
    assert(configuredItem.size, 'size was null');
    url.searchParams.set('designID', configuredItem.design.id.toString());
    url.searchParams.set('formID', configuredItem.form.id.toString());
    url.searchParams.set('insideMaterialID', configuredItem.primaryMaterial.id.toString());
    url.searchParams.set('widthID', configuredItem.width.id.toString());
    url.searchParams.set('sizeID', configuredItem.size.id.toString());
    url.searchParams.set('hasEngraving', (!!configuredItem.engravingText && !!configuredItem.engravingType).toString());
    if (configuredItem.gemstoneArrangement) {
      assert(configuredItem.gemstoneSize, 'No stone size selected');
      const stoneTypeWithShape = await this.getSelectedStoneTypeWithShape(configuredItem);
      const stoneArrangementWithSetting = await this.getselectedStoneArrangementWithSetting(configuredItem);
      url.searchParams.set('stoneTypeWithShapeID', stoneTypeWithShape.id.toString());
      url.searchParams.set('stoneArrangementWithSettingID', stoneArrangementWithSetting.id.toString());
      url.searchParams.set('stoneSizeID', configuredItem.gemstoneSize.id.toString());
    }
    if (retailerIframeID) {
      url.searchParams.set('retailerIframeID', retailerIframeID);
    }
    const validAttributes = await this.webProvider.get<number[]>(url.href);
    assert(validAttributes, 'Failed to fetch attribute');
    return validAttributes;
  }

  async getReachableDesigns(configuredItem: ConfiguredItem, retailerIframeID: string | null): Promise<number[]> {
    const url = new URL(`${this.apiControllerURL}/ReachableDesigns`);
    assert(configuredItem.form, 'form was null');
    assert(configuredItem.primaryMaterial, 'primaryMaterial was null');
    assert(configuredItem.secondaryMaterial, 'secondaryMaterial was null');
    assert(configuredItem.width, 'width was null');
    assert(configuredItem.size, 'size was null');
    url.searchParams.set('formID', configuredItem.form.id.toString());
    url.searchParams.set('insideMaterialID', configuredItem.primaryMaterial.id.toString());
    url.searchParams.set('outsideMaterialID', configuredItem.secondaryMaterial.id.toString());
    url.searchParams.set('widthID', configuredItem.width.id.toString());
    url.searchParams.set('sizeID', configuredItem.size.id.toString());
    url.searchParams.set('hasEngraving', (!!configuredItem.engravingText && !!configuredItem.engravingType).toString());
    if (configuredItem.gemstoneArrangement) {
      assert(configuredItem.gemstoneSize, 'No stone size selected');
      const stoneTypeWithShape = await this.getSelectedStoneTypeWithShape(configuredItem);
      const stoneArrangementWithSetting = await this.getselectedStoneArrangementWithSetting(configuredItem);
      url.searchParams.set('stoneTypeWithShapeID', stoneTypeWithShape.id.toString());
      url.searchParams.set('stoneArrangementWithSettingID', stoneArrangementWithSetting.id.toString());
      url.searchParams.set('stoneSizeID', configuredItem.gemstoneSize.id.toString());
    }
    if (retailerIframeID) {
      url.searchParams.set('retailerIframeID', retailerIframeID);
    }
    const validAttributes = await this.webProvider.get<number[]>(url.href);
    assert(validAttributes, 'Failed to fetch attribute');
    return validAttributes;
  }

  async getReachableSizes(configuredItem: ConfiguredItem, retailerIframeID: string | null): Promise<number[]> {
    const url = new URL(`${this.apiControllerURL}/ReachableSizes`);
    assert(configuredItem.design, 'Design was null');
    assert(configuredItem.form, 'form was null');
    assert(configuredItem.primaryMaterial, 'primaryMaterial was null');
    assert(configuredItem.secondaryMaterial, 'secondaryMaterial was null');
    assert(configuredItem.width, 'width was null');
    url.searchParams.set('designID', configuredItem.design.id.toString());
    url.searchParams.set('formID', configuredItem.form.id.toString());
    url.searchParams.set('insideMaterialID', configuredItem.primaryMaterial.id.toString());
    url.searchParams.set('outsideMaterialID', configuredItem.secondaryMaterial.id.toString());
    url.searchParams.set('widthID', configuredItem.width.id.toString());
    url.searchParams.set('hasEngraving', (!!configuredItem.engravingText && !!configuredItem.engravingType).toString());
    if (configuredItem.gemstoneArrangement) {
      assert(configuredItem.gemstoneSize, 'No stone size selected');
      const stoneTypeWithShape = await this.getSelectedStoneTypeWithShape(configuredItem);
      const stoneArrangementWithSetting = await this.getselectedStoneArrangementWithSetting(configuredItem);
      url.searchParams.set('stoneTypeWithShapeID', stoneTypeWithShape.id.toString());
      url.searchParams.set('stoneArrangementWithSettingID', stoneArrangementWithSetting.id.toString());
      url.searchParams.set('stoneSizeID', configuredItem.gemstoneSize.id.toString());
    }
    if (retailerIframeID) {
      url.searchParams.set('retailerIframeID', retailerIframeID);
    }
    const validAttributes = await this.webProvider.get<number[]>(url.href);
    assert(validAttributes, 'Failed to fetch attribute');
    return validAttributes;
  }

  async getReachableStoneTypesWithShapes(configuredItem: ConfiguredItem, retailerIframeID: string | null): Promise<number[]> {
    const url = new URL(`${this.apiControllerURL}/ReachableStoneTypesWithShapes`);
    assert(configuredItem.design, 'Design was null');
    assert(configuredItem.form, 'form was null');
    assert(configuredItem.primaryMaterial, 'primaryMaterial was null');
    assert(configuredItem.secondaryMaterial, 'secondaryMaterial was null');
    assert(configuredItem.width, 'width was null');
    assert(configuredItem.size, 'size was null');
    url.searchParams.set('designID', configuredItem.design.id.toString());
    url.searchParams.set('formID', configuredItem.form.id.toString());
    url.searchParams.set('insideMaterialID', configuredItem.primaryMaterial.id.toString());
    url.searchParams.set('outsideMaterialID', configuredItem.secondaryMaterial.id.toString());
    url.searchParams.set('widthID', configuredItem.width.id.toString());
    url.searchParams.set('sizeID', configuredItem.size.id.toString());
    url.searchParams.set('hasEngraving', (!!configuredItem.engravingText && !!configuredItem.engravingType).toString());
    if (configuredItem.gemstoneArrangement) {
      assert(configuredItem.gemstoneSize, 'No stone size selected');
      const stoneTypeWithShape = await this.getSelectedStoneTypeWithShape(configuredItem);
      const stoneArrangementWithSetting = await this.getselectedStoneArrangementWithSetting(configuredItem);
      url.searchParams.set('stoneTypeWithShapeID', stoneTypeWithShape.id.toString());
      url.searchParams.set('stoneArrangementWithSettingID', stoneArrangementWithSetting.id.toString());
      url.searchParams.set('stoneSizeID', configuredItem.gemstoneSize.id.toString());
    }
    if (retailerIframeID) {
      url.searchParams.set('retailerIframeID', retailerIframeID);
    }
    const validAttributes = await this.webProvider.get<number[]>(url.href);
    assert(validAttributes, 'Failed to fetch attribute');
    return validAttributes;
  }

  async getReachableStoneArrangementsWithSettings(configuredItem: ConfiguredItem, retailerIframeID: string | null): Promise<number[]> {
    const url = new URL(`${this.apiControllerURL}/ReachableStoneArrangementsWithSettings`);
    assert(configuredItem.design, 'Design was null');
    assert(configuredItem.form, 'form was null');
    assert(configuredItem.primaryMaterial, 'primaryMaterial was null');
    assert(configuredItem.secondaryMaterial, 'secondaryMaterial was null');
    assert(configuredItem.width, 'width was null');
    assert(configuredItem.size, 'size was null');
    url.searchParams.set('designID', configuredItem.design.id.toString());
    url.searchParams.set('formID', configuredItem.form.id.toString());
    url.searchParams.set('insideMaterialID', configuredItem.primaryMaterial.id.toString());
    url.searchParams.set('outsideMaterialID', configuredItem.secondaryMaterial.id.toString());
    url.searchParams.set('widthID', configuredItem.width.id.toString());
    url.searchParams.set('sizeID', configuredItem.size.id.toString());
    url.searchParams.set('hasEngraving', (!!configuredItem.engravingText && !!configuredItem.engravingType).toString());
    if (configuredItem.gemstoneArrangement) {
      assert(configuredItem.gemstoneSize, 'No stone size selected');
      const stoneTypeWithShape = await this.getSelectedStoneTypeWithShape(configuredItem);
      const stoneArrangementWithSetting = await this.getselectedStoneArrangementWithSetting(configuredItem);
      url.searchParams.set('stoneTypeWithShapeID', stoneTypeWithShape.id.toString());
      url.searchParams.set('stoneArrangementWithSettingID', stoneArrangementWithSetting.id.toString());
      url.searchParams.set('stoneSizeID', configuredItem.gemstoneSize.id.toString());
    }
    if (retailerIframeID) {
      url.searchParams.set('retailerIframeID', retailerIframeID);
    }
    const validAttributes = await this.webProvider.get<number[]>(url.href);
    assert(validAttributes, 'Failed to fetch attribute');
    return validAttributes;
  }

  async getReachableStoneSizes(configuredItem: ConfiguredItem, retailerIframeID: string | null): Promise<number[]> {
    const url = new URL(`${this.apiControllerURL}/ReachableStoneSizes`);
    assert(configuredItem.design, 'Design was null');
    assert(configuredItem.form, 'form was null');
    assert(configuredItem.primaryMaterial, 'primaryMaterial was null');
    assert(configuredItem.secondaryMaterial, 'secondaryMaterial was null');
    assert(configuredItem.width, 'width was null');
    assert(configuredItem.size, 'size was null');
    url.searchParams.set('designID', configuredItem.design.id.toString());
    url.searchParams.set('formID', configuredItem.form.id.toString());
    url.searchParams.set('insideMaterialID', configuredItem.primaryMaterial.id.toString());
    url.searchParams.set('outsideMaterialID', configuredItem.secondaryMaterial.id.toString());
    url.searchParams.set('widthID', configuredItem.width.id.toString());
    url.searchParams.set('sizeID', configuredItem.size.id.toString());
    url.searchParams.set('hasEngraving', (!!configuredItem.engravingText && !!configuredItem.engravingType).toString());
    if (configuredItem.gemstoneArrangement) {
      assert(configuredItem.gemstoneSize, 'No stone size selected');
      const stoneTypeWithShape = await this.getSelectedStoneTypeWithShape(configuredItem);
      const stoneArrangementWithSetting = await this.getselectedStoneArrangementWithSetting(configuredItem);
      url.searchParams.set('stoneTypeWithShapeID', stoneTypeWithShape.id.toString());
      url.searchParams.set('stoneArrangementWithSettingID', stoneArrangementWithSetting.id.toString());
      url.searchParams.set('stoneSizeID', configuredItem.gemstoneSize.id.toString());
    }
    if (retailerIframeID) {
      url.searchParams.set('retailerIframeID', retailerIframeID);
    }
    const validAttributes = await this.webProvider.get<number[]>(url.href);
    assert(validAttributes, 'Failed to fetch attribute');
    return validAttributes;
  }

  private async getSelectedStoneTypeWithShape(configuredItem: ConfiguredItem) {
    const stoneTypeWithShape = await this.stoneTypesWithShapesService.values().find(tws => tws.stoneShapeID === configuredItem.gemstoneShape?.id && tws.stoneTypeID === configuredItem.gemstoneType?.id);
    assert(stoneTypeWithShape, 'No matching stone type and shape for selection');
    return stoneTypeWithShape;
  }

    private async getselectedStoneArrangementWithSetting(configuredItem: ConfiguredItem) {
    const stoneArrangementWithSetting = await this.stoneArrangementsWithSettingsService.values().find(tws => tws.stoneArrangementID === configuredItem.gemstoneArrangement?.id && tws.stoneSettingID === configuredItem.gemstoneSetting?.id);
    assert(stoneArrangementWithSetting, 'No matching stone arrangement and setting for selection');
    return stoneArrangementWithSetting;
  }
}
